import { parseApiError } from "utils/errors/parseApiError";

/**
 * Password re-authentication, shared by every screen that gates on it.
 *
 * Shared rather than copied for the same reason as useEmailOtp: the attempt cap is a security
 * control, and a screen-local copy of it is a screen-local way to get it wrong.
 */

/** Wrong current passwords allowed before the session ends. The third one signs the user out. */
export const MAX_REAUTH_ATTEMPTS = 3;

/**
 * The two shapes a wrong password arrives as. With Email Enumeration Protection — the default on
 * current Firebase projects — reauthentication answers `invalid-credential`, and only older
 * projects still answer `wrong-password`.
 */
const WRONG_PASSWORD_CODES = new Set([
  "auth/invalid-credential",
  "auth/wrong-password",
]);

export interface ReauthFailure {
  code: string;
  /** Display-ready text, already resolved by parseApiError. */
  message: string;
  /** A failed guess. Only these spend an attempt — a network blip must not. */
  isWrongPassword: boolean;
}

/**
 * Pass the error from authService.reauthenticate straight in. mapFirebaseError has already
 * flattened it to { code, message }; wrapping it in anything else loses the `auth/...` code and
 * with it the ability to tell a wrong guess from a dead connection.
 */
export function classifyReauthFailure(
  err: unknown,
  fallback: string,
): ReauthFailure {
  const { code, message } = parseApiError(err, fallback);
  return { code, message, isWrongPassword: WRONG_PASSWORD_CODES.has(code) };
}

/**
 * One wrong-password count for the whole tab, shared by every screen that gates on the password
 * (the notification-email modal and /update-password), module-scoped rather than per-component:
 * both of them unmount between guesses, and a per-mount counter hands out a fresh three on every
 * reopen. The sign-out it ends in reloads the page, which is what clears it.
 *
 * A counter the browser holds is a speed bump, not the backstop — that is Firebase's own
 * server-side throttling, which arrives as `auth/too-many-requests`.
 */
let wrongPasswordAttempts = 0;

/** Records a failed guess and returns how many remain. Zero or less means end the session. */
export function spendReauthAttempt(): number {
  wrongPasswordAttempts += 1;
  return MAX_REAUTH_ATTEMPTS - wrongPasswordAttempts;
}

/** Called on a correct password — and by tests, to start each case from a clean count. */
export function clearReauthAttempts(): void {
  wrongPasswordAttempts = 0;
}

/** Copy for a spent guess. `left` is what remains after this one. */
export const attemptsLeftMessage = (message: string, left: number): string =>
  `${message} You have ${left} ${left === 1 ? "attempt" : "attempts"} left.`;

/** Copy for the guess that ends the session. */
export const SIGNED_OUT_MESSAGE =
  "That current password isn't correct. For your security you've been signed out — " +
  "please sign in again.";
